import { Close } from "@mui/icons-material";
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";
import { useAlertContext } from "components/context/AlertContext";
import InfiniteScroll from "components/InfiniteScroll";
import SearchBar from "components/SearchBar";
import { KeyConstant, LangConstant, SystemConstant } from "const";
import { useAccountList, useContactList } from "hooks";
import { uniqBy } from "lodash";
import { saveMessageInQueue } from "pages/HomePage/components/MessengerChat/ConversationContext";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { BranchSelectors } from "redux-store";
import { getInteractor } from "services/local.service";
import { convertString2JSON, isExternalLink, StorageUtil, toSnake } from "utils";
import { getCommonLang, getNSLang } from "utils/lang.utils";
import { getMessageFileList, replaceId2Name } from "utils/message.utils";
import ForwardCategory from "./ForwardCategory";
import PreviewMessage from "./PreviewMessage";

const PAGING_SIZE = 15;
const QUERY_MODE = {
  personal: 0,
  group: 1,
  channel: 2,
  end: 3,
};

const ForwardingDialog = ({ open, onClose, forwardMessage, forwardAttachmentId }) => {
  const prefixKey = StorageUtil.getCurrentPrefixKey();
  const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID, prefixKey);
  const branchId = StorageUtil.getItem(KeyConstant.KEY_BRANCH_ID, prefixKey);

  const classes = useStyles();
  const searchRef = useRef("");
  const queryRef = useRef(true);
  const { showAlert } = useAlertContext();
  const isBranchServer = useSelector(BranchSelectors.isBranchServer);
  const isForwardAttachment = Boolean(forwardAttachmentId);

  const messageFileList = useMemo(() => {
    let messageFileList = getMessageFileList(forwardMessage);
    if (forwardAttachmentId)
      messageFileList = messageFileList.filter(item => item.attachmentId === forwardAttachmentId);

    return messageFileList;
  }, [forwardMessage, forwardAttachmentId]);

  const {
    queryPagination: { page: queryPage, totalPage, totalItems: totalAccountItems },
    getAccountList,
  } = useAccountList();
  const { contactList, isEnd: isEndContactList, getAllContactByName } = useContactList();

  const [channels, setChannels] = useState([]);
  const [groups, setGroups] = useState([]);
  const [personals, setPersonals] = useState([]);
  const [queryMode, setQueryMode] = useState(isBranchServer ? QUERY_MODE.channel : QUERY_MODE.group);
  const [totalItems, setTotalItems] = useState({
    channels: 0,
    groups: 0,
    personals: 0,
  });

  const handleSearch = async searchValue => {
    searchRef.current = searchValue;
    // Reset data
    setChannels([]);
    setGroups([]);
    setPersonals([]);
    setTotalItems({ channels: 0, groups: 0, personals: 0 });

    handleSetTotalItems();
    if (isBranchServer) {
      handleQueryChannel(Boolean(searchValue));
    } else {
      handleQueryGroup(Boolean(searchValue));
    }
  };

  const handleQueryChannel = async isSearchingParams => {
    queryRef.current = true;
    const newChannelList = await getInteractor(prefixKey).LocalGroupService.searchGroupByCondition({
      keyword: searchRef.current,
      group_type: SystemConstant.GROUP_CHAT_TYPE.channel,
      limit: PAGING_SIZE,
      offset: isSearchingParams ? 0 : channels.length,
      account_id: accountId,
      branch_id: branchId,
    });

    const uniqueArray = uniqBy(isSearchingParams ? newChannelList : channels.concat(newChannelList), "id");
    setChannels(uniqueArray);

    const addNumber = PAGING_SIZE - newChannelList.length;
    if (addNumber > 0) {
      setQueryMode(QUERY_MODE.group);
      handleQueryGroup(Boolean(searchRef.current));
    } else {
      setQueryMode(QUERY_MODE.channel);
    }
    queryRef.current = false;
  };

  const handleQueryGroup = async isSearchingParams => {
    queryRef.current = true;
    const newGroupList = await getInteractor(prefixKey).LocalGroupService.searchGroupByCondition({
      keyword: searchRef.current,
      group_type: SystemConstant.GROUP_CHAT_TYPE.group,
      limit: PAGING_SIZE,
      offset: isSearchingParams ? 0 : groups.length,
      account_id: accountId,
      branch_id: branchId,
    });

    const uniqueArray = uniqBy(isSearchingParams ? newGroupList : groups.concat(newGroupList), "id");
    setGroups(uniqueArray);

    const addNumber = PAGING_SIZE - newGroupList.length;
    if (addNumber > 0) {
      setQueryMode(QUERY_MODE.personal);
      handleQueryPersonal(Boolean(searchRef.current));
    } else {
      setQueryMode(QUERY_MODE.group);
    }
    queryRef.current = false;
  };

  const handleQueryPersonal = async isSearching => {
    queryRef.current = true;
    let searchPersonalList = [];
    if (isBranchServer) {
      searchPersonalList = await getAccountList({
        keyword: searchRef.current,
        page: isSearching || personals.length === 0 ? 1 : queryPage + 1,
        isHiddenBot: true,
      });
    } else {
      searchPersonalList = await getAllContactByName(searchRef.current);
    }

    setPersonals(searchPersonalList);
    queryRef.current = false;
  };

  const handleScrollDown = () => {
    // Skip when query data
    const isEndPersonalQuery =
      queryMode === QUERY_MODE.personal &&
      ((isBranchServer && queryPage >= totalPage) || (!isBranchServer && isEndContactList));
    const isSkipQueryData = queryRef.current || isEndPersonalQuery;
    if (isSkipQueryData) return;

    switch (queryMode) {
      case QUERY_MODE.channel:
        handleQueryChannel();
        break;

      case QUERY_MODE.group:
        handleQueryGroup();
        break;

      case QUERY_MODE.personal:
        handleQueryPersonal();
        break;

      default:
        break;
    }
  };

  const handleSetTotalItems = async () => {
    const totalItems = { channels: 0, groups: 0, personals: 0 };

    if (isBranchServer) {
      const totalChannel = await getInteractor(prefixKey).LocalGroupService.count({
        account_id: accountId,
        branch_id: branchId,
        state: SystemConstant.STATE.active,
        group_type: SystemConstant.GROUP_CHAT_TYPE.channel,
        keyword: searchRef.current,
      });

      totalItems.channels = totalChannel;
    }

    const totalGroup = await getInteractor(prefixKey).LocalGroupService.count({
      account_id: accountId,
      branch_id: branchId,
      state: SystemConstant.STATE.active,
      group_type: SystemConstant.GROUP_CHAT_TYPE.group,
      keyword: searchRef.current,
    });
    totalItems.groups = totalGroup;

    setTotalItems(totalItems);
  };

  const handleForward = useCallback(
    async groupId => {
      if (groupId) {
        const attachmentIds = messageFileList.map(item => item.attachmentId);
        const forward = {
          attachment_ids: attachmentIds,
          from_group_id: forwardMessage.groupId,
        };

        const currentGroup = (await getInteractor(prefixKey).LocalGroupService.get(groupId)) || {};
        const mentions = convertString2JSON(forwardMessage.mentions, null);

        let sendType = forwardMessage.sendType;
        let newContent = forwardMessage.content;

        if (FILE_SEND_TYPE.includes(forwardMessage.sendType)) {
          const oldMessageContent = convertString2JSON(forwardMessage.content, {});
          let content_message = isForwardAttachment ? "" : oldMessageContent.content_message;
          if (mentions && content_message) {
            content_message = await replaceId2Name(oldMessageContent.content_message, currentGroup.groupMembers, false);
          }
          const newContentObj = {
            ...oldMessageContent,
            content_message,
          };

          if (oldMessageContent.multiple_file) {
            const multipleFile = messageFileList.map(item => ({
              attachmentId: item.attachmentId,
              metaData: item.metaData,
              aesKeyInfo: item.aesKeyInfo,
            }));
            newContentObj.multiple_file = toSnake(multipleFile);
          }

          newContent = JSON.stringify(newContentObj);
        } else {
          if (mentions) {
            newContent = await replaceId2Name(forwardMessage.content, currentGroup.groupMembers, false);
          }

          if (TEXT_SEND_TYPE.includes(forwardMessage.sendType)) {
            const isPersonal = currentGroup.groupType === SystemConstant.GROUP_CHAT_TYPE.personal;
            sendType = isExternalLink(newContent)
              ? SystemConstant.SEND_TYPE.link
              : isPersonal
                ? SystemConstant.SEND_TYPE.message
                : SystemConstant.SEND_TYPE.groupMessage;
          }
        }

        const forwardOptions = convertString2JSON(forwardMessage.options, {});
        forwardOptions.send_as_direct_message = 0;

        const data2Forward = {
          prefixKey: StorageUtil.getCurrentPrefixKey(),
          groupId: groupId,
          threadId: null,
          sendType: sendType,
          content: newContent,
          options: forwardOptions,
          forward: forward,
        };

        await saveMessageInQueue(data2Forward);
        showAlert({ content: getCommonLang("TXT_FORWARD_SUCCESS") });
      } else {
        showAlert({ content: getCommonLang("TXT_FORWARD_FAILED"), alertProps: { severity: "error" } });
      }

      onClose();
    },
    [forwardMessage, messageFileList],
  );

  useEffect(() => {
    handleSearch();
  }, [isBranchServer]);

  return (
    <Dialog open={open} classes={{ paper: classes.forwardRoot }}>
      <DialogTitle>
        <Stack mt={1}>
          <Typography className={classes.title}>{getCommonLang("TXT_FORWARD")}</Typography>
          <IconButton className={classes.closeBtn} edge="end" onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Box className={classes.forwardPreview}>
          <PreviewMessage
            message={forwardMessage}
            messageFileList={messageFileList}
            isForwardAttachment={isForwardAttachment}
          />
        </Box>

        <SearchBar
          onChange={handleSearch}
          placeholder={getNSLang(LangConstant.NS_HOME, "TXT_CHAT_SEARCH_PLACE_HOLDER")}
          classes={{ root: classes.margin }}
        />

        <InfiniteScroll
          component={Stack}
          className={clsx(classes.margin, classes.forwardSelectList)}
          onScrollToBottom={handleScrollDown}
        >
          {channels.length > 0 && isBranchServer && (
            <ForwardCategory
              type={SystemConstant.GROUP_CHAT_TYPE.channel}
              data={channels}
              totalItems={totalItems.channels}
              onSend={handleForward}
            />
          )}

          {groups.length > 0 && (
            <ForwardCategory
              type={SystemConstant.GROUP_CHAT_TYPE.group}
              data={groups}
              totalItems={totalItems.groups}
              onSend={handleForward}
            />
          )}

          {personals.length > 0 && (
            <ForwardCategory
              type={SystemConstant.GROUP_CHAT_TYPE.personal}
              data={personals}
              totalItems={isBranchServer ? totalAccountItems : contactList.length}
              onSend={handleForward}
            />
          )}
        </InfiniteScroll>
      </DialogContent>
    </Dialog>
  );
};

export default ForwardingDialog;

const FILE_SEND_TYPE = [SystemConstant.SEND_TYPE.file, SystemConstant.SEND_TYPE.image];
const TEXT_SEND_TYPE = [
  SystemConstant.SEND_TYPE.message,
  SystemConstant.SEND_TYPE.groupMessage,
  SystemConstant.SEND_TYPE.botMessage,
  SystemConstant.SEND_TYPE.quote,
];

const useStyles = makeStyles({
  forwardRoot: {
    width: 500,
    height: "fit-content",
    maxHeight: "90%",
  },

  title: {
    fontSize: "1rem",
    fontWeight: 700,
    margin: "auto",
    position: "relative",
  },

  closeBtn: {
    position: "absolute",
    right: 24,
    top: 12,
  },

  forwardPreview: {
    width: "100%",
    height: "fit-content",
    maxHeight: 250,
    borderRadius: 20,
    overflowY: "auto",
  },

  margin: {
    marginTop: 10,
  },

  forwardSelectList: {
    height: 300,
  },
});
